import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuArrowLeft, LuSend, LuMessageCircle, LuInfo, LuLoaderCircle } from "react-icons/lu";
import { ConversationPhoto, conversationTitle } from "./ConversationList";
import { parseApiDate } from "../utils/apiDate";
import "./Messaging.css";

export default function MessageBox({ messages, newMessage, setNewMessage, handleSendMessage, messagesEndRef, selectedConversation, setShowConversationList, senderId, recipientId, sending: parentSending = false, waitingForReply = false }) {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const nearBottom = useRef(true);
  const conversationId = selectedConversation?.conversationId;
  const resolvedRecipient = recipientId || (senderId === selectedConversation?.senderId ? selectedConversation?.receiverId : senderId === selectedConversation?.receiverId ? selectedConversation?.senderId : null);
  const deleted = selectedConversation?.propertyDetails?.isDeleted || selectedConversation?.propertyIsDeleted;
  const unavailable = deleted || !resolvedRecipient;
  // Messaging limit: the API refused another message until the other person answers.
  const locked = unavailable || waitingForReply;
  const sending = submitting || parentSending;
  const locale = i18n.resolvedLanguage || i18n.language || "fr";
  // Shown in the browser time zone (server times are UTC).
  const formatDate = (value, options) => {
    const date = parseApiDate(value);
    return date ? date.toLocaleString(locale, options) : "";
  };
  useEffect(() => { nearBottom.current = true; }, [conversationId]);
  useEffect(() => {
    const container = scrollRef.current;
    if (container && nearBottom.current) container.scrollTop = container.scrollHeight;
  }, [messages, conversationId]);
  useEffect(() => {
    const input = textareaRef.current;
    if (input) { input.style.height = "auto"; input.style.height = Math.min(input.scrollHeight, 120) + "px"; }
  }, [newMessage]);
  const send = async event => {
    event?.preventDefault();
    if (!newMessage.trim() || sending || locked || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    nearBottom.current = true;
    try { await handleSendMessage(); }
    finally { submitLock.current = false; setSubmitting(false); textareaRef.current?.focus(); }
  };
  if (!selectedConversation) return null;
  return <div className="chat-thread">
    <header className="chat-thread-heading">
      <button type="button" className="chat-back" onClick={() => setShowConversationList(true)} aria-label={t("messages.view_conversations", "Voir les conversations")}><LuArrowLeft size={20} /></button>
      <ConversationPhoto conversation={selectedConversation} />
      <div className="chat-heading-copy"><span className="chat-eyebrow">{t("messages.property_discussion", "À propos de cette annonce")}</span><h2>{conversationTitle(selectedConversation)}</h2>
        {selectedConversation.propertyDetails?.adresse && <p>{selectedConversation.propertyDetails.adresse}</p>}
      </div>
    </header>
    <div className="chat-history" ref={scrollRef} role="log" aria-label={t("messages.title", "Messages")} onScroll={event => {
      const node = event.currentTarget; nearBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 90;
    }}>
      {!messages.length && <div className="chat-thread-empty"><span><LuMessageCircle size={30} /></span><h3>{t("messages.start", "La conversation commence ici")}</h3><p>{t("messages.start_hint", "Posez votre première question sur cette annonce.")}</p></div>}
      {messages.map((message, index) => {
        const mine = message.senderId === senderId;
        const day = formatDate(message.timestamp, { day: "numeric", month: "long", year: "numeric" });
        const previousDay = index ? formatDate(messages[index - 1].timestamp, { day: "numeric", month: "long", year: "numeric" }) : "";
        return <React.Fragment key={message.id || index}>
          {day && day !== previousDay && <div className="chat-day"><span>{day}</span></div>}
          <div className={"chat-message-row" + (mine ? " is-mine" : "")}>
            <div className="chat-bubble"><p dir="auto">{message.content}</p><time dateTime={message.timestamp || undefined}>{formatDate(message.timestamp, { hour: "2-digit", minute: "2-digit" })}</time></div>
          </div>
        </React.Fragment>;
      })}
      <div ref={messagesEndRef} />
    </div>
    <div className="chat-composer-area">
      {unavailable && <p className="chat-unavailable" role="status"><LuInfo size={17} /><span>{deleted ? t("messages.deleted_notice", "Cette annonce a été supprimée. Vous pouvez toujours consulter vos échanges.") : t("messages.recipient_unavailable", "Ce destinataire n’est plus disponible. Vous pouvez toujours consulter vos échanges.")}</span></p>}
      {!unavailable && waitingForReply && <p className="chat-unavailable" role="status"><LuInfo size={17} /><span>{t("messages.waiting_for_reply")}</span></p>}
      <form className="chat-composer" onSubmit={send}>
        <textarea ref={textareaRef} rows={1} value={newMessage} disabled={locked} aria-label={t("write_message", "Votre message")} placeholder={t("write_message", "Écrivez votre message…")} onChange={event => setNewMessage(event.target.value)} onKeyDown={event => {
          if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); }
        }} />
        <button type="submit" disabled={sending || locked || !newMessage.trim()} aria-label={t("send", "Envoyer")}>
          {sending ? <LuLoaderCircle size={19} className="animate-spin" /> : <LuSend size={19} />}<span>{t("send", "Envoyer")}</span>
        </button>
      </form>
      <p className="chat-composer-hint">{t("messages.keyboard_hint", "Entrée pour envoyer · Maj + Entrée pour revenir à la ligne")}</p>
    </div>
  </div>;
}
